/**
 * Gemini Quick Wins: /resources/gemini/quick-wins
 *
 * The ten Quick Wins from Caren's Gemini PDF. Same component shape as the
 * Gemini Notebook, Littlebird, Perplexity and Manus Quick Wins pages.
 *
 * Not the same guide as /resources/gemini-notebook/quick-wins. Two different
 * Google products with adjacent names; win 10 here is explicitly about the
 * difference, and mixing them up would be easy and invisible.
 */

import { useState } from "react";
import GuideShell from "@/components/GuideShell";
import { Link } from "wouter";

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  cream: "oklch(0.98 0.008 85)",
  linen: "oklch(0.93 0.01 85)",
  espresso: "oklch(0.22 0.015 65)",
  terracotta: "oklch(0.32 0.08 255)",
  terracottaLight: "oklch(0.32 0.08 255 / 0.08)",
  terracottaMid: "oklch(0.32 0.08 255 / 0.22)",
  border: "oklch(0.88 0.012 85)",
  white: "oklch(1 0.004 85)",
  muted: "oklch(0.52 0.015 65)",
  body: "oklch(0.52 0.015 65)",
};

// ── Data ─────────────────────────────────────────────────────────────────────
interface QuickWin {
  num: number;
  title: string;
  when: string;
  how: string[];
  principle: string;
}

const WINS: QuickWin[] = [
  {
    num: 1,
    title: "Know When to Reach for Gemini",
    when: "Use this when you're choosing between AI tools and want the right one for the job.",
    how: [
      "Reach for Gemini when you want a fast, capable generalist: writing, research, images, code, all in one place",
      "It pulls from the live web, not just training data, so it's strong on current events and real-time facts",
      "Use other tools in your stack for narrower, source-locked work",
      "Choose based on the task, not habit",
    ],
    principle: "The right tool is the one built for the job in front of you.",
  },
  {
    num: 2,
    title: "Talk It Through with Gemini Live",
    when: "Use this when typing is slower than thinking out loud.",
    how: [
      "Start a Live conversation and talk naturally: no need for exact phrasing or commands",
      "Interrupt mid-response to redirect, just like a real conversation",
      "Share your camera or screen so Gemini can see what you're seeing",
      "Use it for brainstorming on the move, not just hands-free convenience",
    ],
    principle: "Thinking out loud is still thinking.",
  },
  {
    num: 3,
    title: "Let Deep Research Do the Digging",
    when: "Use this when a question needs real research, not a quick answer.",
    how: [
      "Turn on Deep Research and give it a clear research question",
      "It searches, reads, and compares sources on its own, then delivers a cited report",
      "Review the sources before you trust the conclusions",
      "Use it for landscape scans, not for topics you already know cold",
    ],
    principle: "Depth is worth waiting for.",
  },
  {
    num: 4,
    title: "Build a Gem for a Task You Repeat",
    when: "Use this when you keep re-explaining the same instructions.",
    how: [
      "Create a Gem with a name, instructions, and any reference files it needs",
      "Set a default mode: chat, Canvas, Deep Research, so it opens ready to work",
      "Reuse it instead of rebuilding your prompt every time",
      "Keep one Gem focused on one job, not a dozen tasks at once",
    ],
    principle: "Build the assistant once. Use it forever.",
  },
  {
    num: 5,
    title: "Draft and Iterate in Canvas",
    when: "Use this when you're shaping a document or piece of code, not just chatting about it.",
    how: [
      "Open Canvas so your draft lives in its own editable panel",
      "Ask for targeted changes: a section, a function, a paragraph, without losing the rest",
      "Watch the document evolve instead of scrolling through chat history",
      "Export when it's ready, not before",
    ],
    principle: "See the work, not just the conversation about it.",
  },
  {
    num: 6,
    title: "Generate Images and Video on the Fly",
    when: "Use this when you need a visual and don't want to leave the conversation.",
    how: [
      "Describe the image or short video you want in plain language",
      "Iterate with follow-up requests instead of starting over",
      "Use it for drafts and concepts, not final production-grade assets",
      "Pull the result straight into whatever you're building",
    ],
    principle: "The right visual finishes the thought.",
  },
  {
    num: 7,
    title: "Bring Gemini Into Gmail, Docs, and Calendar",
    when: "Use this when you want help without switching apps.",
    how: [
      "Open the Gemini side panel inside Gmail, Docs, Sheets, or Slides",
      "Ask it to summarize a thread, draft a reply, or pull details from what's on the page",
      "Let it reference your calendar and inbox for context-aware answers",
      "Turn it off for anything you'd rather keep AI out of",
    ],
    principle: "Help should meet you where you're working.",
  },
  {
    num: 8,
    title: "Ask What's Happening Right Now",
    when: "Use this when you need current information, not historical knowledge.",
    how: [
      "Ask directly about news, prices, or anything time-sensitive",
      "Gemini checks the live web rather than relying on stored knowledge",
      "Verify anything you plan to publish or act on",
      "Use it as a starting point, not a final source",
    ],
    principle: "Current beats confident.",
  },
  {
    num: 9,
    title: "Share Your Screen or Camera Mid-Conversation",
    when: "Use this when it's easier to show than explain.",
    how: [
      "Start a Live session and share your screen or point your camera at what you need help with",
      "Ask questions about exactly what's in view",
      "Use it for troubleshooting, real-world objects, or on-screen content",
      "Close it out and return to text when you don't need the visual context anymore",
    ],
    principle: "Some things are faster to show than to describe.",
  },
  {
    num: 10,
    title: "Know What Gemini Isn't",
    when: "Use this to set expectations before you rely on it for something it's not built for.",
    how: [
      "Gemini pulls broadly from the web: it's not limited to a fixed set of sources",
      "That breadth is a strength for exploration, a weakness for pinpoint accuracy",
      "For deep work confined to your own documents, use a tool built for that",
      "Match the tool's design to what you actually need",
    ],
    principle: "Knowing the edges is part of knowing the tool.",
  },
];

const GROUPS = [
  { label: "Quick Wins 1 – 3", wins: WINS.slice(0, 3) },
  { label: "Quick Wins 4 – 6", wins: WINS.slice(3, 6) },
  { label: "Quick Wins 7 – 10", wins: WINS.slice(6, 10) },
];

// ── Chevron icon ──────────────────────────────────────────────────────────────
function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="16" height="16" viewBox="0 0 16 16" fill="none"
      style={{
        transition: "transform 0.25s ease",
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
        flexShrink: 0,
        color: C.muted,
      }}
    >
      <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Win card ──────────────────────────────────────────────────────────────────
function WinCard({ win, defaultOpen = false }: { win: QuickWin; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{
        background: C.white,
        border: `1px solid ${open ? C.terracottaMid : C.border}`,
        borderLeft: `3px solid ${open ? C.terracotta : C.terracottaMid}`,
        borderRadius: 10,
        overflow: "hidden",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        boxShadow: open
          ? "0 6px 24px oklch(0.32 0.08 255 / 0.10)"
          : hovered
          ? "0 2px 10px rgba(0,0,0,0.06)"
          : "0 1px 4px rgba(0,0,0,0.04)",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          padding: "1.1rem 1.4rem",
          background: open ? C.terracottaLight : "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          transition: "background 0.2s ease",
        }}
      >
        <span
          style={{
            width: 32, height: 32, minWidth: 32,
            borderRadius: "50%",
            background: open ? C.terracotta : C.terracottaLight,
            color: open ? C.white : C.terracotta,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: 13, fontWeight: 700,
            transition: "background 0.2s ease, color 0.2s ease",
            flexShrink: 0,
          }}
        >
          {win.num}
        </span>
        <span
          style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: 16, fontWeight: 600, color: C.espresso,
            flex: 1, lineHeight: 1.35,
          }}
        >
          {win.title}
        </span>
        <ChevronIcon open={open} />
      </button>

      {open && (
        <div
          style={{
            padding: "1.25rem 1.4rem 1.5rem",
            borderTop: `1px solid ${C.terracottaMid}`,
            background: C.white,
          }}
        >
          <div style={{ marginBottom: "1.1rem" }}>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, color: C.terracotta, marginBottom: "0.35rem" }}>
              When This Helps
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.muted, fontStyle: "italic", lineHeight: 1.7 }}>
              {win.when}
            </p>
          </div>

          <div style={{ marginBottom: "1.25rem" }}>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, color: C.terracotta, marginBottom: "0.65rem" }}>
              How
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
              {win.how.map((step, i) => (
                <div key={i} style={{ display: "flex", gap: "0.75rem", alignItems: "flex-start" }}>
                  <span
                    style={{
                      width: 20, height: 20, minWidth: 20,
                      borderRadius: "50%",
                      background: C.linen,
                      border: `1px solid ${C.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                      fontSize: 10, fontWeight: 700, color: C.muted,
                      marginTop: 2, flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </span>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.body, lineHeight: 1.7, margin: 0 }}>
                    {step}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              background: C.terracottaLight,
              border: `1px solid ${C.terracottaMid}`,
              borderLeft: `3px solid ${C.terracotta}`,
              borderRadius: 6,
              padding: "0.75rem 1rem",
            }}
          >
            <span style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, color: C.terracotta, display: "block", marginBottom: "0.3rem" }}>
              Smart Stack Principle
            </span>
            <span style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 14, color: C.espresso, fontStyle: "italic", lineHeight: 1.5 }}>
              {win.principle}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function GeminiQuickWinsPage() {
  return (
    <GuideShell
      context={"Gemini guide"}
      backHref={"/resources/gemini"}
      backLabel={"Back to Gemini"}
    >

      <main style={{ maxWidth: 720, margin: "0 auto", padding: "3.5rem 1.5rem 5rem" }}>

        {/* Hero header */}
        <div style={{ textAlign: "center", marginBottom: "3rem", paddingBottom: "2.5rem", borderBottom: `1px solid ${C.border}` }}>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 11, fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: C.terracotta,
              marginBottom: "1rem",
            }}
          >
            Your Smart Stack Quick Wins
          </p>
          <h1
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: "clamp(28px, 5vw, 40px)",
              fontWeight: 700,
              color: C.espresso,
              lineHeight: 1.2,
              marginBottom: "0.75rem",
            }}
          >
            Gemini
          </h1>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 18,
              color: C.muted,
              fontStyle: "italic",
              marginBottom: "1.5rem",
              lineHeight: 1.5,
            }}
          >
            Your everyday AI. Ready for anything.
          </p>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              background: C.terracottaLight,
              border: `1px solid ${C.terracottaMid}`,
              borderRadius: 20,
              padding: "0.45rem 1.1rem",
            }}
          >
            <span
              style={{
                fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                fontSize: 12, fontWeight: 600,
                color: C.terracotta,
                letterSpacing: "0.04em",
              }}
            >
              Small moves. Clear thinking. Less friction.
            </span>
          </div>
        </div>

        {/* Intro card */}
        <div
          style={{
            background: C.white,
            border: `1px solid ${C.border}`,
            borderLeft: `3px solid ${C.terracotta}`,
            borderRadius: 10,
            padding: "1.75rem 2rem",
            marginBottom: "2.75rem",
          }}
        >
          <h2 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, fontWeight: 700, color: C.espresso, marginBottom: "0.3rem" }}>
            Think broadly.
          </h2>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.muted, fontStyle: "italic", marginBottom: "1.1rem" }}>
            Your general-purpose thinking partner.
          </p>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.body, lineHeight: 1.8, marginBottom: "0.9rem" }}>
            Gemini is built to be your generalist: fast, capable, and connected to the live web. It drafts, researches, creates, and reasons across formats, and it works inside the apps you already use.
          </p>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.body, lineHeight: 1.8, marginBottom: "1.5rem" }}>
            This guide shows how to use it deliberately as your everyday AI: not just a chat window, but a thinking partner that meets you wherever the work is.
          </p>

          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: "1.25rem" }}>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, color: C.terracotta, marginBottom: "0.6rem" }}>
              How to Use This Guide
            </p>
            <p style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 16, fontWeight: 600, color: C.espresso, marginBottom: "0.5rem" }}>
              This is not a checklist.
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.body, lineHeight: 1.75, marginBottom: "0.75rem" }}>
              You don’t need to apply everything here. Each Quick Win is designed for moments when:
            </p>
            {[
              "the right tool isn't obvious",
              "you're switching between apps to get one answer",
              "you want breadth, speed, or a visual, not deep research",
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: "0.65rem", alignItems: "flex-start", marginBottom: "0.45rem" }}>
                <span style={{ color: C.terracotta, fontWeight: 700, fontSize: 15, flexShrink: 0, lineHeight: 1.6 }}>–</span>
                <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.body, lineHeight: 1.7, margin: 0 }}>{item}</p>
              </div>
            ))}
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.body, lineHeight: 1.75, marginTop: "0.85rem" }}>
              Start with the Quick Win that solves the friction you feel right now. Skip the rest.
            </p>
            <p style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 15, fontWeight: 600, color: C.espresso, marginTop: "0.6rem", fontStyle: "italic" }}>
              Versatility is a feature, not a lack of focus.
            </p>
          </div>
        </div>

        {/* Quick Win groups */}
        {GROUPS.map((group) => (
          <div key={group.label} style={{ marginBottom: "2.75rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.1rem" }}>
              <div style={{ height: 1, flex: 1, background: C.border }} />
              <p
                style={{
                  fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                  fontSize: 10, letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  color: C.terracotta,
                  whiteSpace: "nowrap",
                }}
              >
                {group.label}
              </p>
              <div style={{ height: 1, flex: 1, background: C.border }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
              {group.wins.map((win, i) => (
                <WinCard
                  key={win.num}
                  win={win}
                  defaultOpen={i === 0 && group.wins[0].num === 1}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Closing note */}
        <div
          style={{
            background: C.white,
            border: `1px solid ${C.border}`,
            borderLeft: `3px solid ${C.terracotta}`,
            borderRadius: 10,
            padding: "2rem 2.25rem",
            marginBottom: "2.5rem",
          }}
        >
          <h2 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, fontWeight: 700, color: C.espresso, marginBottom: "0.5rem" }}>
            A Note on Versatility
          </h2>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.muted, fontStyle: "italic", marginBottom: "1.1rem", lineHeight: 1.7 }}>
            Gemini works best when you treat it like a generalist, not a specialist.
          </p>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.body, lineHeight: 1.8, marginBottom: "0.5rem" }}>
            It’s the tool for breadth: research, drafting, visuals, and everyday help. It’s not a substitute for depth when depth is what the moment needs.
          </p>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.body, lineHeight: 1.8, marginBottom: "1.25rem" }}>
            Use it to move quickly. Then bring in the tool built for what’s next.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginBottom: "1.75rem" }}>
            {["Stay broad.", "Stay fast.", "Know when to go deeper."].map((line) => (
              <p
                key={line}
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: 16, fontWeight: 600,
                  color: C.espresso,
                  margin: 0,
                  fontStyle: "italic",
                }}
              >
                {line}
              </p>
            ))}
          </div>
          <div
            style={{
              background: C.terracottaLight,
              border: `1px solid ${C.terracottaMid}`,
              borderRadius: 8,
              padding: "1.1rem 1.4rem",
            }}
          >
            <p style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 15, color: C.espresso, fontStyle: "italic", lineHeight: 1.65, marginBottom: "0.6rem" }}>
              “Gemini is the tool I reach for when I need an answer, a draft, or a visual, right now.”
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 13, fontWeight: 700, color: C.terracotta, marginBottom: "0.1rem" }}>
              Caren Glasser
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 11, color: C.muted, letterSpacing: "0.04em" }}>
              Creator of the Smart Stack
            </p>
          </div>
        </div>

        {/* Footer nav */}
        <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
          <Link
            href="/resources/gemini"
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 13, color: C.muted,
              fontWeight: 600,
              textDecoration: "none",
              padding: "0.65rem 1.3rem",
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              background: C.white,
              transition: "border-color 0.2s ease, color 0.2s ease",
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.currentTarget.style.borderColor = C.terracotta;
              e.currentTarget.style.color = C.terracotta;
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.currentTarget.style.borderColor = C.border;
              e.currentTarget.style.color = C.muted;
            }}
          >
            ← Gemini Resources
          </Link>
          <a
            href="https://gemini.google.com/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 13, color: C.white,
              fontWeight: 700,
              textDecoration: "none",
              padding: "0.65rem 1.3rem",
              background: C.terracotta,
              borderRadius: 8,
              letterSpacing: "0.02em",
              transition: "opacity 0.2s ease",
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) =>
              (e.currentTarget.style.opacity = "0.85")
            }
            onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) =>
              (e.currentTarget.style.opacity = "1")
            }
          >
            Try Gemini →
          </a>
        </div>
      </main>


      <style>{`
        @media (max-width: 600px) {
          main { padding: 2rem 1rem 4rem !important; }
        }
      `}</style>
    </GuideShell>
  );
}
